import { useCallback, useRef } from 'react';
import { apolloClient } from '../api/apolloClient';
import { GET_POST_BY_ID_QUERY } from '../graphql/resolvers/postQueries/getPostById.gql';

/**
 * Race-safe optimistic reply-count updater for the replier's own
 * `publishPostReply` mutation.
 *
 * The replier's top icon needs to stay in sync with the drawer without
 * depending on the `new_replies` WS echo (which can arrive arbitrarily
 * relative to the Apollo onCompleted callback). Two invariants:
 *
 *   1. If onCompleted fires first, we bump the Apollo cache from preCount to
 *      preCount + 1. When the WS event lands later it writes the same
 *      absolute value (totalReplyCount) — idempotent.
 *   2. If the WS event fires first, the cache already holds preCount + 1
 *      (or higher, if other users replied concurrently). We must not
 *      over-bump to preCount + 2.
 *
 * The public API is two functions:
 *   - `snapshotBeforePublish(parentPostId)` — call immediately before
 *     invoking the mutation.
 *   - `applyOptimisticBump(parentPostId)` — call in onCompleted (or the
 *     mutation's resolved promise). Writes
 *     `max(current, preCount + 1)` into Post:{id}.replyCount via cache.modify
 *     (TRIL-191).
 */
export function usePublishPostReplyOptimistic() {
  const preBumpCountRef = useRef<number>(0);

  const snapshotBeforePublish = useCallback((parentPostId: string) => {
    // Read the parent post's current replyCount from the Apollo cache.
    // After TRIL-194 the Apollo cache is the sole source of truth for
    // single-post state — the feed list lives in the getUserFeed field
    // policy (TRIL-177), and individual posts are normalized as Post:{id}.
    const cached = apolloClient.cache.readQuery({
      query: GET_POST_BY_ID_QUERY,
      variables: { postId: parentPostId },
    });
    preBumpCountRef.current = cached?.getPostById?.replyCount ?? 0;
  }, []);

  const applyOptimisticBump = useCallback((parentPostId: string) => {
    if (!parentPostId) return;
    const preCount = preBumpCountRef.current;

    // Race-safe increment of Post:{id}.replyCount in the Apollo cache.
    // Uses max(current, preCount+1) so concurrent bumps from other users
    // (already in the cache via the new_replies WS path) aren't clobbered.
    apolloClient.cache.modify({
      id: apolloClient.cache.identify({
        __typename: 'Post',
        id: parentPostId,
      }),
      fields: {
        replyCount(existing) {
          const current =
            typeof existing === 'number' ? existing : (preCount ?? 0);
          return Math.max(current, preCount + 1);
        },
      },
    });
  }, []);

  return { snapshotBeforePublish, applyOptimisticBump };
}
